import { useEffect, useState } from "react";
import { OrderTabs, TableActionButton } from "./components";
import { useOrderList } from "../../../../../hooks/useOrderList";
import { OrderPeriodFilter } from "./components/order-filters";
import {
	ClearButton,
	OrderBatchActions,
	OrderListBody,
	OrderListFilters,
	OrderListHeader,
	OrderListWrapper,
	OrderSearchFilter,
	SearchButton,
} from "./styles";
import { Dropdown } from "primereact/dropdown";
import { Button } from "react-bootstrap";
import { MdClose, MdSearch } from "react-icons/md";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatDate } from "../../../../../helpers";
import formatCurrency from "../../../../utils/formatCurrency";
import { useTranslation } from "react-i18next";
import { PaginationList } from "../../../../components/pagination";
import { Loader } from "../../../../components/loader";
import { Empty } from "../../../../components/empty";
import {
	batchDeleteOrder,
	batchUpdateOrderStatus,
} from "../../../../services/manager/admin/orders.service";
import { OrderStatusCode } from "../../../../models/IOrder";
import { toast } from "react-toastify";
import { Modal } from "../../../../components/modal";

type OrderFilterModel = {
	page: number;
	q: string;
	status?: number | "";
	period?: number;
	year?: number;
};
const resetFilterState: OrderFilterModel = {
	page: 1,
	q: "",
	status: "",
	period: 30,
};

interface IOrderListTableData {
	id: number;
	name: string;
	created_at: string;
	status: string;
	count: number;
	amount: string;
	actions: any;
}

interface IBatchActionOption {
	label: string;
	target_state_string: string;
	target_state_code?: OrderStatusCode;
}

export const Orders: React.FC = () => {
	const { t: translate } = useTranslation();

	//  filters
	const [filter, setFilter] = useState<OrderFilterModel>(resetFilterState);

	const periodFilterOptions = [
		{
			id: 0,
			n: 30,
			label: "nos últimos 30 dias",
		},
		{
			id: 1,
			n: 90,
			label: "nos últimos 3 meses",
		},
		{
			id: 2,
			n: 180,
			label: "nos últimos 6 meses",
		},
		{
			id: 3,
			n: 2023,
			label: "em 2023",
		},
		{
			id: 4,
			n: 2022,
			label: "em 2022",
		},
		{
			id: 5,
			n: 2021,
			label: "em 2021",
		},
	];
	const [periodFilter, setPeriodFilter] = useState(periodFilterOptions[0]);
	const [searchQ, setSearchQ] = useState("");

	const handlePeriodFilterUpdate = (event: any) => {
		setSelectedOrders([]);

		setPeriodFilter(event.value);
		setFilter((current) => {
			// criar uma cópia do state para poder deletar atributos do objeto
			// resetar a pagina
			let clonedState = { ...current, page: 1 };
			// ao enviar os filtros para a API, preciso somente do period ou do year
			// quando o período for maior que um ano, presumo que estou buscando pelo ano
			if (event.value.n <= 365) {
				if (clonedState.year) delete clonedState.year;
				clonedState.period = event.value.n;
			} else {
				if (clonedState.period) delete clonedState.period;
				clonedState.year = event.value.n;
			}
			return clonedState;
		});
	};
	const handleStatusFilterUpdate = (e: any) => {
		setSelectedOrders([]);

		setFilter({ ...filter, status: e.target.name ?? "", page: 1 });
	};

	const handleCommitSearchQ = () => {
		setFilter({ ...filter, q: searchQ, page: 1 });
	};

	const handleSearchClear = () => {
		setSearchQ("");
		setFilter({ ...filter, q: "", page: 1 });
	};

	const handleSearchKeypress = (event: any) => {
		if (event.keyCode === 13) {
			handleCommitSearchQ();
		}
	};

	const handlePageChange = (page: number) => {
		setFilter({ ...filter, page });
		window.scrollTo(0, 0);
	};

	const { listIsLoading, loadOrderList, orderList } = useOrderList(filter);
	const [selectedOrders, setSelectedOrders] = useState<IOrderListTableData[]>(
		[]
	);
	const [tableData, setTableData] = useState<IOrderListTableData[]>();

	/**
	 * Adapter que transforma a resposta da api em dados para a tabela
	 * */
	useEffect(() => {
		let transformed: IOrderListTableData[] = [];

		if (orderList && orderList.results?.length) {
			orderList.results.forEach((anOrder) => {
				transformed.push({
					id: anOrder.id,
					name: anOrder.order_name,
					created_at: formatDate(anOrder.created_at),
					status: translate(
						`manager.orders.status.${anOrder.order_status_display}`
					),
					count: anOrder.total_items,
					amount: formatCurrency(anOrder.amount),
					actions: (
						<TableActionButton
							id={anOrder.id}
							status={anOrder.order_status_display}
							actionPerformedCallback={handleOrderActionTaken}
						></TableActionButton>
					),
				});
			});

			setTableData(transformed);
		}
	}, [orderList]);

	// Açoes em lote
	const batchActionOptions: IBatchActionOption[] = [
		{
			label: "Mudar status para aberto",
			target_state_string: "aberto",
			target_state_code: 1,
		},
		{
			label: "Mudar status para processando",
			target_state_string: "processando",
			target_state_code: 2,
		},
		{
			label: "Mudar status para concluído",
			target_state_string: "concluído",
			target_state_code: 4,
		},
		{
			label: "Mudar status para cancelado",
			target_state_string: "cancelado",
			target_state_code: 3,
		},
		{
			label: "Excluir",
			target_state_string: "DELETED",
		},
	];
	const [availableActionOptions, setAvailableActionOptions] = useState<
		IBatchActionOption[]
	>([]);
	const [selectBatchAction, setSelectBatchAction] =
		useState<IBatchActionOption>();
	const [showBatchDeleteConfirmation, setshowBatchDeleteConfirmation] =
		useState(false);

	// Pedidos com status "Concluído" ou "Cancelado" não podem ter seu status alterado, logo, se um pedido selecionado tiver esse status, a única opção em lote é excluir
	useEffect(() => {
		if (
			selectedOrders &&
			selectedOrders.some(
				(order) =>
					order.status === `Concluído` || order.status === `Cancelado`
			)
		) {
			setAvailableActionOptions([
				batchActionOptions[batchActionOptions.length - 1],
			]);
		} else {
			if (
				selectedOrders &&
				selectedOrders.every((order) => order.status === `Aberto`)
			) {
				// tods os pedidos selecionados tem o mesmo status, posso remover essa opção da lista
				setAvailableActionOptions(
					batchActionOptions.filter(
						(action) => action.target_state_code !== 1
					)
				);
			} else if (
				selectedOrders &&
				selectedOrders.every((order) => order.status === `Processando`)
			) {
				// tods os pedidos selecionados tem o mesmo status, posso remover essa opção da lista
				setAvailableActionOptions(
					batchActionOptions.filter(
						(action) => action.target_state_code !== 2
					)
				);
			} else {
				setAvailableActionOptions(batchActionOptions);
			}
		}
	}, [selectedOrders]);

	const handleOrderActionTaken = () => {
		loadOrderList();
	};

	/**
	 * Controlador das ações em lote
	 */
	const handleCommitBatchAction = () => {
		if (selectBatchAction) {
			if (selectBatchAction.target_state_string === `DELETED`) {
				// mostrar modal de confirmação para deletar
				setshowBatchDeleteConfirmation(true);
			} else {
				// mudar o status em lote
				let ids = selectedOrders.map((order) => order.id);

				batchUpdateOrderStatus(
					ids,
					selectBatchAction.target_state_code!
				).then((response) => {
					toast.success(
						`O status de ${selectedOrders.length} pedido${
							selectedOrders.length > 1 ? "s" : ""
						} foi alterado para ${
							selectBatchAction.target_state_string
						}.`
					);

					// atualizar a lista de pedidos
					handleOrderActionTaken();
					// resetar o valor do dropdown
					setSelectBatchAction(undefined);
				});
			}
		}
	};

	const handleConfirmBatchDelete = () => {
		batchDeleteOrder(selectedOrders.map((order) => order.id)).then(
			(response) => {
				toast.success(
					`${selectedOrders.length} pedido${
						selectedOrders.length > 1 ? "s" : ""
					} foram excluídos.`
				);

				// resetar o valor do dropdown
				setSelectBatchAction(undefined);
				// esconder o modal
				setshowBatchDeleteConfirmation(false);
				// limpar a seleção de pedidos na tabela
				setSelectedOrders([]);
				// atualizar a lista de pedidos
				handleOrderActionTaken();
			}
		);
	};

	return (
		<OrderListWrapper>
			<OrderListHeader>
				<h1>Pedidos</h1>

				{/* mostrar quando tem mais de um item da tabela selecionado */}

				{selectedOrders && selectedOrders.length > 1 && (
					<OrderBatchActions>
						<Dropdown
							value={selectBatchAction}
							options={availableActionOptions}
							onChange={(e) => {
								setSelectBatchAction(e.value);
							}}
							showClear
							placeholder="Ação em lote"
						></Dropdown>
						{/* TODO */}
						<Button
							variant="outline-primary"
							onClick={handleCommitBatchAction}
						>
							Aplicar
						</Button>
					</OrderBatchActions>
				)}
			</OrderListHeader>

			<OrderTabs
				activeItem={filter?.status ?? ""}
				handleChangeCallback={handleStatusFilterUpdate}
			></OrderTabs>

			<OrderListFilters>
				{/* Os filtros de data */}
				<OrderPeriodFilter
					activeItem={periodFilter}
					count={orderList?.count ?? 0}
					filterOptions={periodFilterOptions}
					handleChangeCallback={handlePeriodFilterUpdate}
				></OrderPeriodFilter>
				{/* Busca por número de pedido e comprador */}
				<OrderSearchFilter>
					<input
						value={searchQ}
						type="text"
						placeholder="Pesquisar..."
						onChange={(e) => setSearchQ(e.target.value)}
						onKeyUp={handleSearchKeypress}
					/>
					{!!searchQ && (
						<ClearButton onClick={handleSearchClear}>
							<MdClose size={15} />
						</ClearButton>
					)}
					<SearchButton onClick={handleCommitSearchQ}>
						<MdSearch size={20} />
					</SearchButton>
				</OrderSearchFilter>
			</OrderListFilters>

			{/* -------***--------- */}
			{/* Listagem de pedidos */}
			{/* -------***--------- */}
			<OrderListBody>
				{listIsLoading ? (
					<Loader></Loader>
				) : orderList && orderList.count > 0 ? (
					<DataTable
						dataKey="id"
						value={tableData}
						selectionMode="checkbox"
						selection={selectedOrders}
						onSelectionChange={(e) => setSelectedOrders(e.value)}
					>
						<Column selectionMode="multiple"></Column>
						<Column
							field="name"
							header="Pedido"
							style={{ width: "30%" }}
						></Column>
						<Column
							field="created_at"
							header="Data de compra"
							align={"center"}
						></Column>
						<Column
							field="status"
							header="Status"
							align={"center"}
						></Column>
						<Column
							field="count"
							header="Quant. materiais"
							align={"center"}
						></Column>
						<Column
							field="amount"
							header="Preço"
							align={"center"}
						></Column>
						<Column
							field="actions"
							header="Ações"
							align={"center"}
						></Column>
					</DataTable>
				) : (
					<Empty></Empty>
				)}
			</OrderListBody>

			{/* Paginação  */}
			{!listIsLoading && orderList?.results?.length ? (
				<PaginationList
					currentPage={filter.page ?? 1}
					totalItems={orderList.count ?? 0}
					onPageChange={handlePageChange}
				></PaginationList>
			) : (
				<></>
			)}

			{/* Confirmação de exclusão em massa*/}
			<Modal
				headerTitle="Aviso"
				submitButtonText="Excluir pedidos"
				show={showBatchDeleteConfirmation}
				variant="confirm-modal"
				onHide={() => {
					setshowBatchDeleteConfirmation(false);
				}}
				loading={false}
				onSubmit={handleConfirmBatchDelete}
			>
				<p>
					Tem certeza que deseja <b>excluir</b> os pedidos{" "}
					{selectedOrders.map((order) => order.id).join(", ")}?
				</p>
				<p>Esta ação não pode ser desfeita.</p>
			</Modal>
		</OrderListWrapper>
	);
};
